import React, { useMemo, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useFirestore } from "reactfire";
import MainLayout from "../../layouts/main_layout/main_layout";
import { useFeedback } from "../../providers/feedback/feedback";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";
import {
  parishListMap,
  type DeaneryOption,
  type ParishOption,
} from "../../utils/deanery_parishes";
import DycConfirmationModal from "./components/dyc_confirmation_modal";
import DycRegistrationForm from "./components/dyc_registration_form";
import DycSubmissionSuccessModal from "./components/dyc_submission_success_modal";
import { pitchBattleLastDateLabel, pitchBattleParticipantOptions, sanitizePhone } from "./data";
import {
  generateDycRegistrationPdf,
  type PitchBattlePdfData,
} from "./utils/generateDycRegistrationPdf";
import { sendReceiptEmail } from "./utils/sendReceiptEmail";

type PitchBattleLocationState = {
  selectedDeanery?: DeaneryOption | null;
  selectedParish?: ParishOption | null;
  presidentName?: string;
  animatorName?: string;
  presidentContactNumber?: string;
  animatorContactNumber?: string;
  presidentEmail?: string;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


export default function PitchBattleRegistrationPage() {
  const firestore = useFirestore();
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const location = useLocation();
  const locationState = (location.state || {}) as PitchBattleLocationState;

  const [selectedDeanery, setSelectedDeanery] = useState<DeaneryOption | null>(
    locationState.selectedDeanery || null,
  );
  const [selectedParish, setSelectedParish] = useState<ParishOption | null>(
    locationState.selectedParish || null,
  );
  const [presidentName, setPresidentName] = useState(locationState.presidentName || "");
  const [animatorName, setAnimatorName] = useState(locationState.animatorName || "");
  const [presidentContactNumber, setPresidentContactNumber] = useState(
    locationState.presidentContactNumber || "",
  );
  const [animatorContactNumber, setAnimatorContactNumber] = useState(
    locationState.animatorContactNumber || "",
  );
  const [presidentEmail, setPresidentEmail] = useState(locationState.presidentEmail || "");
  const [totalParticipantsCount, setTotalParticipantsCount] = useState("1");
  const [entryType, setEntryType] = useState<"individual" | "group">("individual");
  const [participantCount, setParticipantCount] = useState("1");
  const [participantNames, setParticipantNames] = useState(["", "", "", "", ""]);
  const [teamLeadContact, setTeamLeadContact] = useState("");
  const [ideaTitle, setIdeaTitle] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<{
    open: boolean;
    blobUrl: string;
    fileName: string;
    parishName: string;
    recipientEmail: string;
  }>({
    open: false,
    blobUrl: "",
    fileName: "",
    parishName: "",
    recipientEmail: "",
  });

  const parishOptions = useMemo(
    () => (selectedDeanery ? parishListMap[selectedDeanery.name] || [] : []),
    [selectedDeanery],
  );

  const handleParticipantCountChange = (value: string) => {
    setParticipantCount(value);
    setTotalParticipantsCount(value);
    if (value === "1") {
      setEntryType("individual");
    }
  };

  const validate = () => {
    if (!selectedDeanery || !selectedParish) {
      return "Please select the deanery and parish.";
    }
    if (!presidentName.trim() || !animatorName.trim()) {
      return "President name and animator name are required.";
    }
    if (presidentContactNumber.length !== 10 || animatorContactNumber.length !== 10) {
      return "Both contact numbers must be 10 digits.";
    }
    if (!emailRegex.test(presidentEmail.trim())) {
      return "Please enter a valid president email address.";
    }
    if (!ideaTitle.trim()) {
      return "Pitch title is required.";
    }
    if (teamLeadContact.length !== 10) {
      return "Team lead contact number must be 10 digits.";
    }

    const names = participantNames
      .slice(0, Number(participantCount || "0"))
      .map((name) => name.trim())
      .filter(Boolean);

    if (names.length !== Number(participantCount || "0")) {
      return "Please enter the participant names for Pitch Battle.";
    }

    return "";
  };

  const handleOpenSubmission = () => {
    const validationError = validate();
    setFormError(validationError);

    if (validationError) {
      showSnackbar(validationError, "#f44336");
      return;
    }

    setShowConfirmationModal(true);
  };

  const executeSubmit = async () => {
    setIsSubmitting(true);

    try {
      const parishName = selectedParish?.value || "parish";

      // Prepare data & generate PDF receipt
      const pdfData: PitchBattlePdfData = {
        type: "pitch_battle",
        selectedDeanery,
        selectedParish,
        presidentName,
        presidentContactNumber,
        presidentEmail,
        animatorName,
        animatorContactNumber,
        ideaTitle,
        entryType,
        teamLeadContact,
        participantCount,
        participantNames,
      };

      const { doc: pdfDoc, blobUrl, fileName } = generateDycRegistrationPdf(pdfData);

      const pitchBattlePayload = {
        createdAt: Date().toString(),
        updatedAt: Date().toString(),
        deanery: selectedDeanery,
        parish: selectedParish,
        presidentName: presidentName.trim(),
        animatorName: animatorName.trim(),
        presidentContactNumber,
        animatorContactNumber,
        contactNumber: presidentContactNumber,
        email: presidentEmail.trim(),
        entryType,
        participantCount: Number(participantCount),
        participantNames: participantNames
          .slice(0, Number(participantCount))
          .map((name) => name.trim()),
        teamLeadContact,
        ideaTitle: ideaTitle.trim(),
      };

      // Store in dedicated dyc_pitch_battle collection
      await addDoc(collection(firestore, "dyc_pitch_battle"), pitchBattlePayload);

      // Also map and store in main dyc_yuva_dabazo collection
      await addDoc(collection(firestore, "dyc_yuva_dabazo"), {
        ...pitchBattlePayload,
        totalParticipantsCount: Number(participantCount),
        selectedEvents: ["pitchbattle"],
        isPitchBattleEntry: true,
        events: {
          pitchbattle: {
            entryType,
            ideaTitle: ideaTitle.trim(),
            participantCount: Number(participantCount),
            participants: participantNames
              .slice(0, Number(participantCount))
              .map((name) => name.trim()),
            teamLeadContact,
          },
        },
      });

      // Auto-download PDF
      pdfDoc.save(fileName);

      // Automatically email copy to user and organizers
      const currentEmail = presidentEmail.trim();
      sendReceiptEmail({
        recipientEmail: currentEmail,
        parishName,
        deaneryName: selectedDeanery?.name,
        presidentName: presidentName.trim(),
        presidentContactNumber,
        animatorName: animatorName.trim(),
        animatorContactNumber,
        registrationType: "pitch_battle",
        doc: pdfDoc,
        fileName,
      });

      showSnackbar(`Yuva Dabazo 2026 Pitch Battle registration of ${parishName} completed`);
      setFormError("");
      setShowConfirmationModal(false);

      // Open post-submission PDF view/download modal
      setSuccessModalData({
        open: true,
        blobUrl,
        fileName,
        parishName,
        recipientEmail: currentEmail,
      });

      // Clear all unit and pitch battle form data
      setSelectedDeanery(null);
      setSelectedParish(null);
      setPresidentName("");
      setAnimatorName("");
      setPresidentContactNumber("");
      setAnimatorContactNumber("");
      setPresidentEmail("");
      setIdeaTitle("");
      setTeamLeadContact("");
      setParticipantCount("1");
      setParticipantNames(["", "", "", "", ""]);
      setEntryType("individual");
      setTotalParticipantsCount("1");

      // Clear React Router location state so state cannot reload from history
      navigate(location.pathname, { replace: true, state: null });
    } catch (error) {
      setFormError("Unable to submit Pitch Battle registration right now.");
      showSnackbar("Unable to submit Pitch Battle registration right now.", "#f44336");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <Stack spacing={3.5} my={{ xs: 4, md: 6 }}>
        <Box>
          <Typography variant="h3" fontWeight={900} color={MAIN_THEME_COLOR}>
            Pitch Battle Registration
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Last date for registration: {pitchBattleLastDateLabel}. Multiple entries are allowed from one unit.
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 4,
            border: `1px solid ${SECONDARY_THEME_COLOR}33`,
            backgroundColor: "#fff7ef",
          }}
        >
          <CardContent>
            <Typography fontWeight={800}>Pitch Battle Note</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              This event can be registered as an individual entry or as a group of 2 to 5 members.
            </Typography>
          </CardContent>
        </Card>

        <DycRegistrationForm
          selectedDeanery={selectedDeanery}
          selectedParish={selectedParish}
          parishOptions={parishOptions}
          presidentName={presidentName}
          animatorName={animatorName}
          presidentContactNumber={presidentContactNumber}
          animatorContactNumber={animatorContactNumber}
          presidentEmail={presidentEmail}
          totalParticipantsCount={totalParticipantsCount}
          onDeaneryChange={(value) => {
            setSelectedDeanery(value);
            setSelectedParish(null);
          }}
          onParishChange={setSelectedParish}
          onPresidentNameChange={setPresidentName}
          onAnimatorNameChange={setAnimatorName}
          onPresidentContactNumberChange={setPresidentContactNumber}
          onAnimatorContactNumberChange={setAnimatorContactNumber}
          onPresidentEmailChange={setPresidentEmail}
          onTotalParticipantsCountChange={setTotalParticipantsCount}
          showTotalParticipantsCount={false}
          showPaymentDetails={false}
        />

        <Card sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
              Pitch Battle Specifics
            </Typography>
            <Grid container spacing={3} mt={0.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  select
                  label="Entry Type"
                  value={entryType}
                  onChange={(event) => setEntryType(event.target.value as "individual" | "group")}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="group">Group</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  select
                  label="Number of Participants"
                  value={participantCount}
                  onChange={(event) => handleParticipantCountChange(event.target.value)}
                >
                  {pitchBattleParticipantOptions
                    .filter((count) => (entryType === "individual" ? count === "1" : count !== "1"))
                    .map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Pitch Title"
                  value={ideaTitle}
                  onChange={(event) => setIdeaTitle(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Team Lead Contact No"
                  value={teamLeadContact}
                  onChange={(event) => setTeamLeadContact(sanitizePhone(event.target.value))}
                  inputProps={{ inputMode: "numeric", maxLength: 15 }}
                />
              </Grid>
              {Array.from({ length: Number(participantCount) }).map((_, index) => (
                <Grid item xs={12} md={6} key={`pitch-member-${index + 1}`}>
                  <TextField
                    fullWidth
                    required
                    label={`Participant ${index + 1} Name`}
                    value={participantNames[index] || ""}
                    onChange={(event) =>
                      setParticipantNames((current) =>
                        current.map((name, nameIndex) =>
                          nameIndex === index ? event.target.value : name,
                        ),
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {formError ? (
              <Typography color="error" variant="body2" mt={2}>
                {formError}
              </Typography>
            ) : null}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="center">
          <Button
            type="button"
            onClick={handleOpenSubmission}
            disabled={isSubmitting}
            sx={{
              minWidth: 220,
              py: 1.4,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            {isSubmitting ? "Submitting..." : "Final Submission"}
          </Button>
        </Box>
      </Stack>

      <DycConfirmationModal
        type="pitch_battle"
        open={showConfirmationModal}
        isSubmitting={isSubmitting}
        selectedDeanery={selectedDeanery}
        selectedParish={selectedParish}
        presidentName={presidentName}
        presidentContactNumber={presidentContactNumber}
        presidentEmail={presidentEmail}
        animatorName={animatorName}
        animatorContactNumber={animatorContactNumber}
        ideaTitle={ideaTitle}
        entryType={entryType}
        teamLeadContact={teamLeadContact}
        participantCount={participantCount}
        participantNames={participantNames}
        onConfirm={executeSubmit}
        onEdit={() => setShowConfirmationModal(false)}
      />

      <DycSubmissionSuccessModal
        open={successModalData.open}
        onClose={() => setSuccessModalData((current) => ({ ...current, open: false }))}
        fileName={successModalData.fileName}
        blobUrl={successModalData.blobUrl}
        parishName={successModalData.parishName}
        recipientEmail={successModalData.recipientEmail}
        isPitchBattle={true}
        onDownload={() => {
          if (successModalData.blobUrl && successModalData.fileName) {
            const link = document.createElement("a");
            link.href = successModalData.blobUrl;
            link.download = successModalData.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }}
      />
    </MainLayout>
  );
}

